import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Trash2, Image as ImageIcon, Eye, EyeOff, X, Save } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SaveBar } from '@/components/ui/save-bar';
import { toast } from '@/components/ui/sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/cn';
import { useBrief, useUpdateBriefSlice, useMe } from '@/lib/brief';
import { RichTextEditor } from '@/components/rich-text/editor';
import { MediaPicker } from '@/components/media/picker';
import { useMediaHost, type MediaAsset } from '@/lib/media';

export const Route = createFileRoute('/_app/tresci/aktualnosci')({
  staticData: { crumb: 'Aktualności' },
  component: AktualnosciPage,
});

interface Post {
  slug: string;
  title: string;
  excerpt?: string;
  body_html: string;
  cover_url?: string;
  cover_alt?: string;
  author_name?: string;
  reading_time?: string;
  tags?: string[];
  category?: string;
  published_at?: string;
  published?: boolean;
  created_at?: number;
  updated_at?: number;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l').replace(/Ł/g, 'l')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function readingTime(html: string): string {
  const words = html.replace(/<[^>]+>/g, ' ').trim().split(/\s+/).filter(Boolean).length;
  const m = Math.max(1, Math.round(words / 200));
  return `${m} min czytania`;
}

function AktualnosciPage() {
  const { data: brief } = useBrief();
  const me = useMe();
  const update = useUpdateBriefSlice();
  const posts = ((brief?.posts ?? []) as Post[]) || [];

  const [activeSlug, setActiveSlug] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState<Post | null>(null);
  const [dirty, setDirty] = React.useState(false);
  const [saveStatus, setSaveStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');
  const [confirmDelete, setConfirmDelete] = React.useState<string | null>(null);

  // Reset draft when the user picks a different post
  React.useEffect(() => {
    if (!activeSlug) { setDraft(null); setDirty(false); return; }
    const p = posts.find((x) => x.slug === activeSlug);
    if (p) {
      setDraft({ ...p });
      setDirty(false);
    }
  }, [activeSlug, posts.length]);

  const newPost = () => {
    const now = Math.floor(Date.now() / 1000);
    const base: Post = {
      slug: `wpis-${now.toString(36)}`,
      title: 'Nowy wpis',
      excerpt: '',
      body_html: '<p>Zacznij pisać…</p>',
      published: false,
      published_at: new Date().toISOString().slice(0, 10),
      tags: [],
      created_at: now,
      updated_at: now,
    };
    setActiveSlug(base.slug);
    setDraft(base);
    setDirty(true);
  };

  const saveDraft = async (): Promise<void> => {
    if (!draft) return;
    if (!draft.title || draft.title.length < 3) {
      toast.error('Tytuł musi mieć min. 3 znaki.');
      return;
    }
    if (!draft.body_html || draft.body_html.length < 20) {
      toast.error('Treść musi mieć min. 20 znaków.');
      return;
    }
    const cleaned: Post = {
      ...draft,
      slug: draft.slug || slugify(draft.title),
      reading_time: readingTime(draft.body_html),
      updated_at: Math.floor(Date.now() / 1000),
    };
    const others = posts.filter((p) => p.slug !== cleaned.slug && p.slug !== activeSlug);
    if (others.some((p) => p.slug === cleaned.slug)) {
      toast.error('Slug już istnieje — wybierz inny.');
      return;
    }
    const next = [...others, cleaned];
    try {
      await update.mutateAsync({ slice: 'posts', data: next });
      setActiveSlug(cleaned.slug);
      setDraft(cleaned);
      setDirty(false);
      setSaveStatus('saved');
      toast.success('Zapisano wpis');
    } catch (e) {
      setSaveStatus('error');
      toast.error('Nie udało się zapisać: ' + (e as Error).message);
    }
  };

  const togglePublish = async (post: Post) => {
    const next = posts.map((p) => (p.slug === post.slug ? { ...p, published: !p.published } : p));
    try {
      await update.mutateAsync({ slice: 'posts', data: next });
      toast.success(post.published ? 'Ukryto wpis' : 'Opublikowano wpis');
    } catch (e) {
      toast.error('Nie udało się zmienić statusu: ' + (e as Error).message);
    }
  };

  const removePost = async (slug: string) => {
    const next = posts.filter((p) => p.slug !== slug);
    try {
      await update.mutateAsync({ slice: 'posts', data: next });
      if (activeSlug === slug) setActiveSlug(null);
      toast.success('Wpis usunięty');
    } catch (e) {
      toast.error('Nie udało się usunąć: ' + (e as Error).message);
    } finally {
      setConfirmDelete(null);
    }
  };

  const tenantSlug = me.data?.tenant.slug;

  return (
    <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
      <aside className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-text-subtle">
            {posts.length} {posts.length === 1 ? 'wpis' : 'wpisów'}
          </h2>
          <Button type="button" size="sm" onClick={newPost}>
            <Plus className="size-4" aria-hidden="true" /> Nowy wpis
          </Button>
        </div>
        {posts.length === 0 ? (
          <Card>
            <CardContent className="py-6">
              <p className="text-sm text-text-muted">
                Brak wpisów. Kliknij <strong>Nowy wpis</strong>, aby dodać pierwszy.
              </p>
            </CardContent>
          </Card>
        ) : (
          <ul role="list" className="space-y-1.5">
            {posts
              .slice()
              .sort((a, b) => (b.updated_at ?? 0) - (a.updated_at ?? 0))
              .map((p) => {
                const active = p.slug === activeSlug;
                return (
                  <li key={p.slug}>
                    <button
                      type="button"
                      onClick={() => setActiveSlug(p.slug)}
                      className={cn(
                        'flex w-full items-start gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                        active
                          ? 'border-accent/30 bg-accent/10'
                          : 'border-border bg-surface hover:bg-bg',
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-text">{p.title}</span>
                        <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-text-subtle">
                          <code className="font-mono">/aktualnosci/{p.slug}</code>
                        </span>
                      </span>
                      {p.published === false ? (
                        <Badge variant="soft" className="shrink-0">szkic</Badge>
                      ) : (
                        <Badge variant="success" className="shrink-0">live</Badge>
                      )}
                    </button>
                  </li>
                );
              })}
          </ul>
        )}
      </aside>

      <section className="min-w-0">
        {!draft ? (
          <Card>
            <CardContent className="py-12">
              <p className="text-center text-sm text-text-muted">
                Wybierz wpis po lewej lub kliknij <strong>Nowy wpis</strong>, aby zacząć.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <CardTitle>{draft.title || 'Nowy wpis'}</CardTitle>
                  <CardDescription>
                    Publiczny adres: <code>https://{tenantSlug ?? 'twoja-klinika'}.pozyskajpacjenta.pl/aktualnosci/{draft.slug || 'slug'}</code>
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => togglePublish(draft)}
                  >
                    {draft.published === false ? (
                      <><Eye className="size-3.5" aria-hidden="true" /> Opublikuj</>
                    ) : (
                      <><EyeOff className="size-3.5" aria-hidden="true" /> Ukryj</>
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmDelete(draft.slug)}
                  >
                    <Trash2 className="size-3.5" aria-hidden="true" /> Usuń
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <PostEditor
                value={draft}
                onChange={(next) => {
                  setDraft(next);
                  setDirty(true);
                }}
              />

              <SaveBar
                className="mt-6"
                saving={update.isPending}
                dirty={dirty}
                status={saveStatus}
                onCancel={() => {
                  const orig = posts.find((p) => p.slug === activeSlug);
                  if (orig) {
                    setDraft({ ...orig });
                    setDirty(false);
                  } else {
                    setActiveSlug(null);
                  }
                }}
                onSave={saveDraft}
              />
            </CardContent>
          </Card>
        )}
      </section>

      {confirmDelete ? (
        <AlertDialog open={true} onOpenChange={(o) => !o && setConfirmDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Usunąć wpis?</AlertDialogTitle>
              <AlertDialogDescription>
                Wpis zniknie z publicznej witryny po najbliższej publikacji. Tej operacji nie da się cofnąć.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Anuluj</AlertDialogCancel>
              <AlertDialogAction onClick={() => confirmDelete && removePost(confirmDelete)}>
                Usuń
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  );
}

function PostEditor({
  value,
  onChange,
}: {
  value: Post;
  onChange: (next: Post) => void;
}) {
  const mediaHost = useMediaHost();
  const [coverOpen, setCoverOpen] = React.useState(false);

  const onPickCover = (asset: MediaAsset) => {
    onChange({
      ...value,
      cover_url: `${mediaHost}${asset.public_url}`,
      cover_alt: asset.alt_text ?? '',
    });
    setCoverOpen(false);
  };
  const clearCover = () => {
    onChange({ ...value, cover_url: undefined, cover_alt: undefined });
    setCoverOpen(false);
  };

  return (
    <div className="space-y-5">
      <Field label="Tytuł" required>
        <Input
          value={value.title}
          onChange={(e) => {
            const title = e.currentTarget.value;
            onChange({
              ...value,
              title,
              slug: value.slug && value.slug !== 'wpis-' && !value.slug.startsWith('wpis-') ? value.slug : slugify(title),
            });
          }}
          placeholder={'Np. „Otwarcie nowej pracowni RTG"'}
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Slug (URL)" hint="Tylko małe litery, cyfry i myślniki.">
          <Input
            value={value.slug}
            onChange={(e) => onChange({ ...value, slug: slugify(e.currentTarget.value) })}
            placeholder="otwarcie-pracowni-rtg"
          />
        </Field>
        <Field label="Kategoria (opcjonalnie)">
          <Input
            value={value.category ?? ''}
            onChange={(e) => onChange({ ...value, category: e.currentTarget.value || undefined })}
            placeholder="Aktualności / Sprzęt / Profilaktyka"
          />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Autor (opcjonalnie)">
          <Input
            value={value.author_name ?? ''}
            onChange={(e) => onChange({ ...value, author_name: e.currentTarget.value || undefined })}
            placeholder="lek. dent. Marek Łuczak"
          />
        </Field>
        <Field label="Data publikacji" hint="Format ISO YYYY-MM-DD">
          <Input
            type="date"
            value={value.published_at ?? ''}
            onChange={(e) => onChange({ ...value, published_at: e.currentTarget.value || undefined })}
          />
        </Field>
      </div>

      <Field label="Zajawka" hint="Pokazywana na liście artykułów i w meta description.">
        <Textarea
          value={value.excerpt ?? ''}
          onChange={(e) => onChange({ ...value, excerpt: e.currentTarget.value || undefined })}
          rows={2}
          maxLength={280}
          placeholder="Krótkie podsumowanie — 1–2 zdania."
        />
      </Field>

      <Field label="Zdjęcie hero">
        <div className="flex items-start gap-4">
          {value.cover_url ? (
            <img
              src={value.cover_url}
              alt={value.cover_alt ?? ''}
              className="size-32 rounded-md object-cover ring-1 ring-border"
            />
          ) : (
            <div className="grid size-32 place-items-center rounded-md bg-surface text-text-subtle ring-1 ring-border ring-dashed">
              <ImageIcon className="size-6" aria-hidden="true" />
            </div>
          )}
          <div className="flex flex-1 flex-col gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => setCoverOpen(true)}>
              <ImageIcon className="size-3.5" aria-hidden="true" />
              {value.cover_url ? 'Zmień zdjęcie' : 'Wybierz z biblioteki'}
            </Button>
            {value.cover_url ? (
              <Button type="button" size="sm" variant="ghost" onClick={clearCover}>
                <X className="size-3.5" aria-hidden="true" /> Usuń zdjęcie
              </Button>
            ) : null}
            <Input
              value={value.cover_alt ?? ''}
              onChange={(e) => onChange({ ...value, cover_alt: e.currentTarget.value || undefined })}
              placeholder="Opis alternatywny (alt)"
            />
          </div>
        </div>
      </Field>

      <Field label="Treść" hint="Edytor WYSIWYG — formatowanie, listy, linki, obrazy.">
        <RichTextEditor
          value={value.body_html}
          onChange={(html) => onChange({ ...value, body_html: html })}
          placeholder="Napisz pełną treść artykułu…"
          minHeight={360}
        />
      </Field>

      <Field label="Tagi" hint={'Oddziel przecinkami: np. „prewencja, RTG, dzieci"'}>
        <Input
          value={(value.tags ?? []).join(', ')}
          onChange={(e) => {
            const arr = e.currentTarget.value
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
              .slice(0, 10);
            onChange({ ...value, tags: arr });
          }}
          placeholder="prewencja, sprzęt, ortodoncja"
        />
      </Field>

      {coverOpen ? (
        <MediaPicker
          title="Wybierz zdjęcie hero"
          onSelect={onPickCover}
          onClose={() => setCoverOpen(false)}
          onClear={clearCover}
          selectedId={null}
        />
      ) : null}
    </div>
  );
}
